import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { BIBLE_BOOKS, TRANSLATIONS } from "../lib/bibleBooks";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { ChevronLeft, ChevronRight, BookOpen, Search, Menu, Play, Pause, Square, Volume2, Heart } from "lucide-react";
import { toast } from "sonner";
import useSpeech from "../lib/useSpeech";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Slider } from "../components/ui/slider";
import { isBookmarked as bmIs, toggle as bmToggle } from "../lib/bookmarks";

function BookList({ selectedBook, onSelect }) {
  const [filter, setFilter] = useState("");
  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    if (!f) return BIBLE_BOOKS;
    return BIBLE_BOOKS.filter((b) => b.name.toLowerCase().includes(f));
  }, [filter]);
  const ot = filtered.filter((b) => b.testament === "OT");
  const nt = filtered.filter((b) => b.testament === "NT");

  const render = (list, label) => (
    <div>
      <div className="text-xs uppercase tracking-widest text-stone-500 mb-2 mt-4 first:mt-0">{label}</div>
      <div className="grid grid-cols-2 gap-1">
        {list.map((b) => (
          <button
            key={b.name}
            data-testid={`book-${b.name.replace(/\s+/g, "-").toLowerCase()}`}
            onClick={() => onSelect(b.name)}
            className={`text-left rounded px-2 py-1.5 text-sm transition-colors ${
              selectedBook === b.name ? "bg-primary text-primary-foreground" : "text-stone-700 hover:bg-stone-100"
            }`}
          >
            {b.name}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div className="pr-2">
      <div className="relative mb-2">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" strokeWidth={1.5} />
        <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter books..." className="pl-8 h-9" data-testid="book-filter" />
      </div>
      {ot.length > 0 && render(ot, "Old Testament")}
      {nt.length > 0 && render(nt, "New Testament")}
    </div>
  );
}

function VerseSpan({ verse, translation }) {
  const v = { ...verse, translation_id: translation };
  const [saved, setSaved] = useState(() => bmIs(v));
  const onToggle = (e) => {
    e.stopPropagation();
    const now = bmToggle(v);
    setSaved(now);
    toast.success(now ? "Verse bookmarked" : "Bookmark removed");
  };
  return (
    <span data-testid={`verse-${verse.chapter}-${verse.verse}`} className="group relative">
      <span className="verse-num">{verse.verse}</span>
      {verse.text.trim()}
      <button
        onClick={onToggle}
        className={`ml-1 mr-1 inline-flex items-center align-middle opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity ${saved ? "opacity-100" : ""}`}
        title={saved ? "Remove bookmark" : "Bookmark verse"}
        data-testid={`bookmark-toggle-${verse.chapter}-${verse.verse}`}
      >
        <Heart className={`h-3.5 w-3.5 ${saved ? "text-primary fill-primary" : "text-stone-400"}`} strokeWidth={1.5} />
      </button>{" "}
    </span>
  );
}

export default function BibleReader() {
  const [params, setParams] = useSearchParams();
  const [book, setBook] = useState(params.get("book") || "John");
  const [chapter, setChapter] = useState(parseInt(params.get("chapter") || "3", 10));
  const [translation, setTranslation] = useState(params.get("translation") || "kjv");
  const [refInput, setRefInput] = useState(params.get("ref") || "");
  const [passage, setPassage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const bookMeta = useMemo(() => BIBLE_BOOKS.find((b) => b.name === book), [book]);
  const maxChapters = bookMeta?.chapters || 1;

  const fetchPassage = useCallback(async (reference, tr) => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/bible/passage", { params: { reference, translation: tr } });
      setPassage(data);
      // Sync book/chapter from the returned passage
      const v0 = data?.verses?.[0];
      if (v0?.book_name) {
        const found = BIBLE_BOOKS.find((b) => b.name.toLowerCase() === v0.book_name.toLowerCase());
        if (found) {
          setBook(found.name);
          if (typeof v0.chapter === "number") setChapter(v0.chapter);
        }
      }
    } catch (e) {
      setErr(formatApiError(e));
      setPassage(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Sync URL and fetch
  useEffect(() => {
    const rawRef = params.get("ref");
    if (rawRef) {
      // Free-form reference like "John 3:16"
      setRefInput(rawRef);
      fetchPassage(rawRef, translation);
      // Try to parse book+chapter from it
      const m = rawRef.match(/^(\d?\s?[A-Za-z ]+?)\s+(\d+)(?::\d+.*)?$/);
      if (m) {
        const parsedBook = m[1].trim();
        const found = BIBLE_BOOKS.find((b) => b.name.toLowerCase() === parsedBook.toLowerCase());
        if (found) {
          setBook(found.name);
          setChapter(parseInt(m[2], 10));
        }
      }
    } else {
      const paramBook = params.get("book");
      const paramChapter = params.get("chapter");
      if (paramBook) setBook(paramBook);
      if (paramChapter) setChapter(parseInt(paramChapter, 10));
      const ref = `${paramBook || book} ${paramChapter || chapter}`;
      fetchPassage(ref, translation);
      setRefInput(ref);
    }
    // Re-run when URL params change (e.g., navigating from Home with a new ref)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const loadChapter = (b, c, tr = translation) => {
    setBook(b);
    setChapter(c);
    const ref = `${b} ${c}`;
    setRefInput(ref);
    setParams({ book: b, chapter: String(c), translation: tr });
    fetchPassage(ref, tr);
  };

  const submitRef = (e) => {
    e.preventDefault();
    const r = refInput.trim();
    if (!r) return;
    setParams({ ref: r, translation });
    fetchPassage(r, translation);
    const m = r.match(/^(\d?\s?[A-Za-z ]+?)\s+(\d+)(?::\d+.*)?$/);
    if (m) {
      const parsedBook = m[1].trim();
      const found = BIBLE_BOOKS.find((b) => b.name.toLowerCase() === parsedBook.toLowerCase());
      if (found) { setBook(found.name); setChapter(parseInt(m[2], 10)); }
    }
  };

  const handleTranslation = (val) => {
    setTranslation(val);
    const ref = refInput.trim() || `${book} ${chapter}`;
    fetchPassage(ref, val);
  };

  const prevChapter = () => { if (chapter > 1) loadChapter(book, chapter - 1); };
  const nextChapter = () => { if (chapter < maxChapters) loadChapter(book, chapter + 1); };

  const speech = useSpeech();
  const passageText = useMemo(() => {
    if (!passage?.verses) return "";
    return passage.verses.map((v) => `Verse ${v.verse}. ${v.text.trim()}`).join(" ");
  }, [passage]);

  // Stop speech when passage changes
  useEffect(() => { speech.stop(); /* eslint-disable-next-line */ }, [passage?.reference, translation]);

  // Autoplay from ?autoplay=1 query param (fires once per unique ref navigation)
  const autoplay = params.get("autoplay") === "1";
  const autoplayFiredRef = React.useRef("");
  useEffect(() => {
    const key = `${params.get("ref") || `${book}-${chapter}`}|${translation}`;
    if (autoplay && passageText && speech.supported && autoplayFiredRef.current !== key) {
      autoplayFiredRef.current = key;
      const t = setTimeout(() => speech.play(passageText), 250);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoplay, passageText, speech.supported, params]);

  return (
    <div className="fade-in grid grid-cols-1 md:grid-cols-[240px_1fr] gap-8">
      {/* Sidebar - desktop */}
      <aside className="hidden md:block">
        <div className="sticky top-24 max-h-[calc(100vh-8rem)] overflow-y-auto rounded-md border border-stone-200 bg-white p-4">
          <BookList selectedBook={book} onSelect={(b) => loadChapter(b, 1)} />
        </div>
      </aside>

      {/* Content */}
      <div className="min-w-0">
        {/* Top controls */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <Sheet>
            <SheetTrigger asChild className="md:hidden">
              <Button variant="outline" size="sm" data-testid="mobile-books-btn"><Menu className="h-4 w-4 mr-1" strokeWidth={1.5} /> Books</Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-80 overflow-y-auto">
              <div className="mt-6"><BookList selectedBook={book} onSelect={(b) => loadChapter(b, 1)} /></div>
            </SheetContent>
          </Sheet>

          <form onSubmit={submitRef} className="flex flex-1 min-w-[240px] gap-2">
            <div className="relative flex-1">
              <BookOpen className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" strokeWidth={1.5} />
              <Input
                value={refInput}
                onChange={(e) => setRefInput(e.target.value)}
                placeholder="Jump to reference — e.g. Genesis 1, John 3:16, Romans 8:28-30"
                className="pl-9 h-10 bg-white"
                data-testid="reference-input"
              />
            </div>
            <Button type="submit" data-testid="reference-go-btn">Go</Button>
          </form>

          <Select value={translation} onValueChange={handleTranslation}>
            <SelectTrigger className="w-[170px]" data-testid="translation-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TRANSLATIONS.map((t) => (
                <SelectItem key={t.code} value={t.code} data-testid={`translation-${t.code}`}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Chapter chips */}
        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">{book} — chapters</div>
          <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto pr-1">
            {Array.from({ length: maxChapters }, (_, i) => i + 1).map((c) => (
              <button
                key={c}
                data-testid={`chapter-${c}`}
                onClick={() => loadChapter(book, c)}
                className={`chip h-8 px-2.5 text-xs ${chapter === c ? "chip-active" : ""}`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        {/* Passage */}
        <div className="rounded-md border border-stone-200 bg-white">
          {/* Audio bar */}
          {speech.supported && (
            <div className="flex items-center gap-2 border-b border-stone-200 bg-stone-50/60 px-4 py-2" data-testid="audio-bar">
              {speech.state !== "playing" ? (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => (speech.state === "paused" ? speech.resume() : speech.play(passageText))}
                  disabled={!passageText}
                  data-testid="audio-play"
                >
                  <Play className="h-4 w-4 mr-1" strokeWidth={1.5} />
                  {speech.state === "paused" ? "Resume" : "Listen"}
                </Button>
              ) : (
                <Button size="sm" variant="outline" onClick={speech.pause} data-testid="audio-pause">
                  <Pause className="h-4 w-4 mr-1" strokeWidth={1.5} /> Pause
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={speech.stop} disabled={speech.state === "idle"} data-testid="audio-stop">
                <Square className="h-4 w-4" strokeWidth={1.5} />
              </Button>
              <div className="ml-auto flex items-center gap-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button size="sm" variant="ghost" data-testid="audio-settings">
                      <Volume2 className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="w-72">
                    <div className="space-y-4">
                      <div>
                        <div className="text-xs uppercase tracking-widest text-stone-500 mb-1">Voice</div>
                        <select
                          value={speech.voiceURI}
                          onChange={(e) => speech.setVoiceURI(e.target.value)}
                          className="w-full h-9 rounded-md border border-stone-200 bg-white px-2 text-sm"
                          data-testid="audio-voice-select"
                        >
                          {speech.voices.map((v) => (
                            <option key={v.voiceURI} value={v.voiceURI}>{v.name} ({v.lang})</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <div className="flex items-center justify-between text-xs text-stone-500 mb-1">
                          <span className="uppercase tracking-widest">Speed</span>
                          <span>{speech.rate.toFixed(2)}x</span>
                        </div>
                        <Slider
                          min={0.5} max={1.5} step={0.05}
                          value={[speech.rate]}
                          onValueChange={(v) => speech.setRate(v[0])}
                          data-testid="audio-rate-slider"
                        />
                      </div>
                      <p className="text-xs text-stone-500 leading-relaxed">
                        Uses your device's built-in text-to-speech. Voice quality varies by browser and OS.
                      </p>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between p-4 border-b border-stone-200">
            <Button variant="ghost" size="sm" onClick={prevChapter} disabled={chapter <= 1} data-testid="prev-chapter">
              <ChevronLeft className="h-4 w-4 mr-1" strokeWidth={1.5} /> Prev
            </Button>
            <div className="text-sm text-stone-600" data-testid="passage-title">
              {passage?.reference || `${book} ${chapter}`} · <span className="uppercase">{translation}</span>
            </div>
            <Button variant="ghost" size="sm" onClick={nextChapter} disabled={chapter >= maxChapters} data-testid="next-chapter">
              Next <ChevronRight className="h-4 w-4 ml-1" strokeWidth={1.5} />
            </Button>
          </div>

          <div className="p-6 sm:p-10 lg:p-14">
            {loading && <div className="text-stone-500" data-testid="passage-loading">Loading passage…</div>}
            {err && !loading && (
              <div className="text-destructive" data-testid="passage-error">{err}</div>
            )}
            {!loading && !err && passage && (
              <div className="scripture max-w-[70ch] mx-auto" data-testid="passage-text">
                {passage.verses?.map((v) => (
                  <VerseSpan key={`${v.book_id}-${v.chapter}-${v.verse}`} verse={v} translation={translation} />
                ))}
              </div>
            )}
          </div>

          {passage?.translation_note && (
            <div className="border-t border-stone-200 p-4 text-xs text-stone-500 text-center">{passage.translation_note}</div>
          )}
        </div>
      </div>
    </div>
  );
}
